import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Banknote, MessageCircle, ExternalLink, Wallet, Receipt, Send,
  Loader2, CalendarDays, Stethoscope, Phone, Mail, ArrowRight,
  Clock, CheckCircle2,
} from "lucide-react";
import PatientAvatar from "@/components/PatientAvatar";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const DAYS = (d) => (d === 0 ? "Today" : `${d} day${d === 1 ? "" : "s"}`);

/**
 * OutstandingDrawer — right-side drawer that surfaces a single patient's
 * outstanding details without leaving the receivables page. Data is fetched
 * inline (invoices + ledger for payment history) so the user stays in
 * context. All actions (Collect, Send Reminder) route through the same APIs
 * the parent uses, plus a "View Billing" that pops the patient's billing
 * page in a new tab so the receivables list stays open.
 */
export default function OutstandingDrawer({
  open, onClose, row, doctorMap = {}, onRemind, onCollect,
}) {
  const [pending, setPending] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    if (!open || !row?.patient_id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [inv, pays] = await Promise.all([
          api.get(`/patients/${row.patient_id}/invoices`).catch(() => ({ data: [] })),
          api.get(`/patients/${row.patient_id}/payments`).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setPending(
            (inv.data || []).filter((i) => ["pending", "overdue", "partial"].includes(i.status))
          );
          setPayments((pays.data || []).slice(0, 10));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, row?.patient_id]);

  if (!row) return null;
  const doctorName = doctorMap[row.primary_doctor_id || row.assigned_doctor_id] || "Unassigned";

  const remind = async () => {
    setReminding(true);
    try {
      await api.post(`/outstanding/${row.patient_id}/remind`);
      toast.success(`Reminder sent to ${row.name}`);
      onRemind?.(row);
    } catch (e) {
      toast.error(apiError(e, "Reminder failed"));
    } finally { setReminding(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!max-w-[520px] bg-white p-0 flex flex-col"
                    data-testid="outstanding-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E8EAE9]">
          <div className="flex items-start gap-4">
            <PatientAvatar patient={{ id: row.patient_id, name: row.name }} size="lg" />
            <div className="flex-1 min-w-0">
              <SheetTitle className="font-display text-2xl leading-tight truncate">
                {row.name}
              </SheetTitle>
              <SheetDescription className="sr-only" data-testid="outstanding-drawer-description">
                Outstanding balance and payment history for {row.name}.
              </SheetDescription>
              <div className="mt-1 text-xs text-ink-mid flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {doctorName}</span>
                {row.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-pine" /> {row.phone}</span>}
                {row.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {row.email}</span>}
              </div>
            </div>
          </div>

          {/* Outstanding summary */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <SummaryTile label="Outstanding" value={INR(row.outstanding)}
                         tone="clay" testid="drawer-outstanding" />
            <SummaryTile label="Oldest" value={DAYS(row.days_overdue)}
                         tone="rose" testid="drawer-age" />
            <SummaryTile label="Wallet" value={INR(row.wallet_balance || 0)}
                         tone={row.wallet_balance > 0 ? "purple" : "neutral"} testid="drawer-wallet" />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Pending invoices */}
          <section>
            <SectionTitle icon={Receipt}>Pending invoices ({pending.length || row.invoices?.length || 0})</SectionTitle>
            {loading ? (
              <SectionSkeleton />
            ) : (pending.length > 0 ? pending : row.invoices || []).slice(0, 8).map((inv) => (
              <div key={inv.id} className="mt-2 flex items-center justify-between p-3 rounded-xl border border-[#E8EAE9] hover:bg-linen/40">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink truncate">
                    {inv.invoice_number || `Invoice ${(inv.id || "").slice(-6).toUpperCase()}`}
                  </div>
                  <div className="text-[11px] text-ink-soft flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {inv.days != null ? DAYS(inv.days) : (inv.due_date ? new Date(inv.due_date).toLocaleDateString("en-IN") : "—")}
                    {inv.status && <span className="uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded-full bg-[#FDF3ED] text-[#8A3F1E]">{inv.status}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-clay">{INR(inv.remaining ?? inv.total)}</div>
                  {inv.total != null && inv.remaining != null && inv.remaining !== inv.total && (
                    <div className="text-[10px] text-ink-soft">of {INR(inv.total)}</div>
                  )}
                </div>
              </div>
            ))}
            {(!loading && (pending.length || row.invoices?.length || 0) === 0) && (
              <div className="mt-2 text-xs text-ink-soft italic">No pending invoices.</div>
            )}
          </section>

          {/* Payment timeline */}
          <section>
            <SectionTitle icon={CalendarDays}>Payment timeline</SectionTitle>
            {loading ? <SectionSkeleton /> : payments.length === 0 ? (
              <div className="mt-2 text-xs text-ink-soft italic">No prior payments on record.</div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {payments.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between text-xs py-1.5 px-3 rounded-lg hover:bg-linen/40">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-pine" />
                      <span className="text-ink-mid">
                        {new Date(p.paid_at || p.created_at || p.ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      {p.method && (
                        <span className="text-[10px] uppercase tracking-wider text-ink-soft">{p.method}</span>
                      )}
                    </div>
                    <span className="font-mono text-ink">{INR(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Reminder history — placeholder for future backend log */}
          <section>
            <SectionTitle icon={Send}>Reminder history</SectionTitle>
            <div className="mt-2 text-xs text-ink-soft italic">
              {row.last_reminder_at
                ? `Last reminder ${new Date(row.last_reminder_at).toLocaleDateString("en-IN")}`
                : "No reminders sent yet."}
            </div>
          </section>
        </div>

        {/* Sticky action footer */}
        <div className="border-t border-[#E8EAE9] px-6 py-4 flex flex-wrap items-center gap-2 bg-white">
          <button
            onClick={() => onCollect?.(row)}
            data-testid="drawer-collect"
            className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2"
          >
            <Banknote className="w-4 h-4" /> Collect payment
          </button>
          <button
            onClick={remind}
            disabled={reminding || !row.phone}
            data-testid="drawer-remind"
            className="text-sm px-4 py-2 rounded-full border border-[#25D366]/30 text-[#1DA850] hover:bg-[#E8F5EE] inline-flex items-center gap-2 disabled:opacity-50"
          >
            {reminding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Send reminder
          </button>
          <Link
            to={`/app/billing?patient_id=${row.patient_id}`}
            target="_blank"
            data-testid="drawer-view-billing"
            className="text-sm px-4 py-2 rounded-full border border-[#E8EAE9] text-ink hover:bg-linen inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" /> View billing
          </Link>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="ml-auto text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen"
          >
            Close
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---- Sub-components ---------------------------------------------------------
function SummaryTile({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-linen/60 text-ink-mid",
    clay:    "bg-[#FDF3ED] text-[#8A3F1E]",
    rose:    "bg-[#F6E3E5] text-[#7A2A2E]",
    purple:  "bg-[#F0E7EE] text-[#4A2148]",
  };
  return (
    <div className={`rounded-xl px-3 py-2.5 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="mt-0.5 font-mono text-sm">{value}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink-soft">
      {Icon && <Icon className="w-3 h-3" />} {children}
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="mt-2 space-y-2 animate-pulse">
      {[0,1,2].map((i) => (
        <div key={i} className="h-11 rounded-xl bg-linen/70" />
      ))}
    </div>
  );
}
